import { useState, useMemo } from 'react';
import { Box } from '@mui/material';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { Drawer, drawerType } from '../../../../../packages/ui/organisms/drawer';
import { Input, inputType, inputSize } from '../../../../../packages/ui/atoms/input';
import { iconBtnType, IconButton } from '../../../../../packages/ui/molecules/icon-button';
import { Icons } from '../../../../../packages/icons';
import { colors } from '../../../../../packages/colors';
import MedicationCard from '../../../member-detail/components/Medications/medication-card';
import { useStyles } from '../history-drawer/HistoryDrawer.styles';
import EmptyState from '../empty-state';
import { ProfileInfoRole } from '../../../../../components/v2/ProfileInfo';
import { getProfile } from '../../../../../redux/modules/profile/selectors';
import { getAuth } from '../../../../../redux/modules/auth/selectors';
import {
  isExternalRxRecord,
  flattenExternalRxRecords,
  sortMedicationsWithExternalLast,
} from '../../../../../utils/medicationUtils';

const MedicationDrawer = ({
  open,
  isNoDrawer,
  onClose,
  onClickNewReport,
  isOnlyView,
  onClickCard,
  onClickEdit,
  items = [],
  appointment,
  loading,
  isSession = true,
}) => {
  const classes = useStyles();
  const { isAdmin } = useSelector(getAuth);
  const { profile } = useSelector(getProfile);
  const [search, setSearch] = useState('');

  // Flatten external RX records
  const flattenedRecords = useMemo(() => {
    return flattenExternalRxRecords(items, isExternalRxRecord);
  }, [items]);

  // Apply search and sorting
  const list = useMemo(() => {
    const filteredList = flattenedRecords.filter(item =>
      item.name.toLowerCase().includes(search.toLowerCase()),
    );
    return sortMedicationsWithExternalLast(filteredList);
  }, [flattenedRecords, search]);

  const renderContent = () => {
    return (
      <Box className={classes.container}>
        <Box className={classes.searchWrap}>
          <Input
            variant={inputType.SEARCH}
            className={classes.searchInput}
            placeholder="Search"
            size={inputSize.M}
            value={search}
            onChange={setSearch}
          />
          {(profile?.designation === ProfileInfoRole.NURSE_PRACTITIONER ||
            profile?.designation === ProfileInfoRole.PHYSICIAN ||
            isAdmin) && (
            <IconButton
              variant={iconBtnType.PRIMARY}
              className={classes.reportNewBtn}
              onClick={onClickNewReport}
            >
              Report new
            </IconButton>
          )}
        </Box>
        <Box className={classes.items}>
          {loading ? (
            <Box display="flex" justifyContent="center" paddingY="10px">
              <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
            </Box>
          ) : list.length > 0 ? (
            list.map((item, index) => {
              const isExternal = isExternalRxRecord(item);
              // Create a custom onClick handler that routes to the correct handler
              const handleClick = (medication, isEdit) => {
                if (isEdit && isExternal && onClickEdit) {
                  onClickEdit(medication);
                } else if (onClickCard) {
                  onClickCard(medication, isEdit);
                }
              };
              return (
                <MedicationCard key={index} medication={item} onClick={handleClick} isOnlyView={isOnlyView} />
              );
            })
          ) : (
            <EmptyState
              title={`No Medications prescribed for ${appointment?.participantName || 'this member'} yet.`}
              subtitle="If you're ready to prescribe Medications for them, you can do so here."
              glyph="face-talking-interested"
            />
          )}
        </Box>
      </Box>
    );
  };

  if (isNoDrawer) {
    return renderContent();
  }

  return (
    <Drawer
      open={open}
      title={isSession ? 'Medications' : 'Active medications'}
      subTitle={loading ? '' : `${list.length} total`}
      onClose={onClose}
      variant={drawerType.FORM}
      className={clsx(classes.drawer, classes.drawerWithSubtitle)}
      footer={<span />}
    >
      {renderContent()}
    </Drawer>
  );
};

export { MedicationDrawer };
